/**
 * The generic Entity 360 sections. Every entity inherits these; none of them knows what entity it is
 * looking at beyond an `entityType` string and an id.
 *
 * Documents deserve a note. A storage path is never rendered and never returned by the API: the object
 * itself is reached through a short-lived signed URL that checks the permission and logs the access. The
 * reason is that these buckets hold KYC documents, bank details and HR letters, and a path that leaks into
 * a screenshot or a browser history is a path someone can try.
 */
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck, Ban, CircleAlert, Clock, Download, FileText, Hourglass, Link2, RefreshCcw, ShieldAlert,
} from 'lucide-react';
import { rpc } from '../lib/supabase';
import {
  EmptyState, ErrorState, LoadingRows, SectionLabel, Status, StatusBadge, Unknown, cx,
} from '../ui/primitives';
import { DataGrid, type Column } from '../ui/DataGrid';
import { EntityTimeline } from './Timeline';
import type { EntityApproval, EntityDocument, EntityProvenance } from './types';
import { date as fmtDate, dateTime as fmtDateTime } from '../ui/format';

/* ── Documents ───────────────────────────────────────────────────────────────────────────────────── */

const CLASSIFICATION: Record<EntityDocument['classification'], { label: string; restricted: boolean }> = {
  PUBLIC_INTERNAL: { label: 'Internal', restricted: false },
  CONFIDENTIAL: { label: 'Confidential', restricted: false },
  RESTRICTED_PII: { label: 'Restricted · personal', restricted: true },
  RESTRICTED_FINANCIAL: { label: 'Restricted · financial', restricted: true },
};

function formatBytes(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function DocumentsSection({ entityType, entityId }: { entityType: string; entityId: string }) {
  const query = useQuery({
    queryKey: ['entity-documents', entityType, entityId],
    queryFn: () => rpc<EntityDocument[]>('entity_documents', { p_entity_type: entityType, p_entity_id: entityId }),
  });

  const columns: Column<EntityDocument>[] = [
    {
      key: 'file',
      header: 'Document',
      cell: (d) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <FileText size={13} className="shrink-0 text-ink-muted" aria-hidden />
          <span className="truncate">{d.file_name}</span>
          {d.superseded_at ? <span className="label-text shrink-0">superseded</span> : null}
        </span>
      ),
      sortValue: (d) => d.file_name,
    },
    { key: 'type', header: 'Type', width: '10rem', cell: (d) => d.document_type_name, sortValue: (d) => d.document_type_name },
    {
      key: 'classification',
      header: 'Classification',
      width: '11rem',
      cell: (d) => {
        const meta = CLASSIFICATION[d.classification];
        return meta.restricted ? (
          <StatusBadge tone="refused" icon={ShieldAlert}>
            {meta.label}
          </StatusBadge>
        ) : (
          <span className="text-small text-ink-muted">{meta.label}</span>
        );
      },
      sortValue: (d) => d.classification,
    },
    {
      key: 'upload',
      header: 'Upload',
      width: '8rem',
      cell: (d) =>
        d.upload_status === 'STORED' ? (
          <span className="text-small text-ink-muted">Stored</span>
        ) : (
          // A row can exist before its bytes do. Showing it as present would be a lie.
          <StatusBadge tone={d.upload_status === 'PENDING_UPLOAD' ? 'queued' : 'refused'} icon={Clock}>
            {d.upload_status.replace('_', ' ').toLowerCase()}
          </StatusBadge>
        ),
      sortValue: (d) => d.upload_status,
    },
    {
      key: 'verification',
      header: 'Verification',
      width: '10rem',
      cell: (d) =>
        d.verification_status === 'VERIFIED' ? (
          <StatusBadge tone="accepted" icon={BadgeCheck} title={d.verified_by ? `by ${d.verified_by}` : undefined}>
            Verified
          </StatusBadge>
        ) : d.verification_status === 'REJECTED' ? (
          <StatusBadge tone="refused" icon={Ban} title={d.verification_note ?? undefined}>
            Rejected
          </StatusBadge>
        ) : (
          <StatusBadge tone="queued" icon={Hourglass}>
            Unverified
          </StatusBadge>
        ),
      sortValue: (d) => d.verification_status,
    },
    {
      key: 'validity',
      header: 'Valid to',
      width: '9rem',
      cell: (d) =>
        d.valid_to ? (
          <span className={cx(d.is_expired && 'font-medium text-refused')}>
            {fmtDate(d.valid_to)}
            {d.is_expired ? ' · expired' : ''}
          </span>
        ) : null,
      sortValue: (d) => d.valid_to,
    },
    { key: 'size', header: 'Size', width: '6rem', numeric: true, cell: (d) => formatBytes(d.size_bytes), sortValue: (d) => d.size_bytes },
    {
      key: 'uploaded',
      header: 'Uploaded',
      width: '11rem',
      cell: (d) => (
        <span title={d.uploaded_by ? `by ${d.uploaded_by}` : undefined}>
          {fmtDate(d.uploaded_at)}
        </span>
      ),
      sortValue: (d) => d.uploaded_at,
    },
    {
      key: 'open',
      header: '',
      width: '3rem',
      align: 'center',
      cell: (d) =>
        d.upload_status === 'STORED' ? (
          <button
            type="button"
            aria-label={`Open ${d.file_name}`}
            title="Opening issues a short-lived signed URL and records the access"
            className="text-ink-muted hover:text-brand"
            // Wired in EPIC 04, when there is a signed-URL command to call. Disabled rather than absent,
            // so the affordance is visible and its state is honest.
            disabled
          >
            <Download size={13} />
          </button>
        ) : null,
    },
  ];

  return (
    <DataGrid
      rows={query.data}
      columns={columns}
      rowKey={(d) => d.id}
      isLoading={query.isLoading}
      error={query.error}
      onRetry={() => void query.refetch()}
      storageKey={`documents.${entityType}`}
      emptyTitle="No documents"
      emptyDetail="Documents registered against this record appear here with their classification, verification state and expiry."
      footnote="Opening a document issues a short-lived signed URL; the storage path is never exposed."
    />
  );
}

/* ── Approvals ───────────────────────────────────────────────────────────────────────────────────── */

export const APPROVAL_STATUS: Record<
  EntityApproval['status'],
  { tone: 'accepted' | 'queued' | 'refused' | 'neutral' | 'info'; icon: typeof Hourglass; word: string }
> = {
  PENDING: { tone: 'queued', icon: Hourglass, word: 'Awaiting approval' },
  APPROVED: { tone: 'accepted', icon: BadgeCheck, word: 'Approved' },
  REJECTED: { tone: 'refused', icon: Ban, word: 'Rejected' },
  ESCALATED: { tone: 'queued', icon: CircleAlert, word: 'Escalated' },
  CANCELLED: { tone: 'neutral', icon: Ban, word: 'Cancelled' },
  EXECUTED: { tone: 'accepted', icon: BadgeCheck, word: 'Executed' },
  FAILED: { tone: 'refused', icon: CircleAlert, word: 'Failed' },
};

export function ApprovalsSection({ entityType, entityId }: { entityType: string; entityId: string }) {
  const query = useQuery({
    queryKey: ['entity-approvals', entityType, entityId],
    queryFn: () => rpc<EntityApproval[]>('entity_approvals', { p_entity_type: entityType, p_entity_id: entityId }),
  });

  if (query.isLoading) return <LoadingRows rows={3} columns={3} />;
  if (query.isError) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  if ((query.data ?? []).length === 0) {
    return (
      <EmptyState
        title="No approvals"
        detail="An action on this record that needs a decision appears here, with who asked, who decided and why."
      />
    );
  }

  return (
    <ul className="divide-y divide-line">
      {query.data!.map((approval) => {
        const meta = APPROVAL_STATUS[approval.status];
        return (
          <li key={approval.id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="code-text">{approval.action_code}</span>
                {approval.reason ? (
                  <p className="mt-0.5 text-small text-ink-muted italic">“{approval.reason}”</p>
                ) : null}
              </div>
              <StatusBadge tone={meta.tone} icon={meta.icon}>
                {meta.word}
              </StatusBadge>
            </div>

            <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-small md:grid-cols-4">
              <div>
                <dt className="label-text">Requested by</dt>
                <dd>{approval.requested_by ?? <Unknown />}</dd>
              </div>
              <div>
                <dt className="label-text">Requested at</dt>
                <dd>{fmtDateTime(approval.requested_at)}</dd>
              </div>
              <div>
                <dt className="label-text">Decided at</dt>
                <dd>{approval.decided_at ? fmtDateTime(approval.decided_at) : <Unknown />}</dd>
              </div>
              <div>
                <dt className="label-text">Executed at</dt>
                <dd>{approval.executed_at ? fmtDateTime(approval.executed_at) : <Unknown />}</dd>
              </div>
            </dl>

            {approval.actions.length > 0 ? (
              <ul className="mt-2 space-y-0.5 border-l-2 border-line pl-3">
                {approval.actions.map((action, i) => (
                  <li key={i} className="text-small text-ink-muted">
                    <span className="font-medium text-ink">{action.decision}</span> ·{' '}
                    {action.decided_by ?? 'unknown'} · {fmtDateTime(action.occurred_at)}
                    {action.note ? <span className="italic"> — “{action.note}”</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}

            {approval.error ? <p className="mt-2 text-small text-refused">{approval.error}</p> : null}
          </li>
        );
      })}
    </ul>
  );
}

/* ── Integration provenance ──────────────────────────────────────────────────────────────────────── */

export function ProvenanceSection({ entityType, entityId }: { entityType: string; entityId: string }) {
  const query = useQuery({
    queryKey: ['entity-provenance', entityType, entityId],
    queryFn: () => rpc<EntityProvenance[]>('entity_provenance', { p_entity_type: entityType, p_entity_id: entityId }),
  });

  if (query.isLoading) return <LoadingRows rows={2} columns={3} />;
  if (query.isError) return <ErrorState error={query.error} retry={() => void query.refetch()} />;

  const links = query.data ?? [];
  if (links.length === 0) {
    return (
      <EmptyState
        title="Created in the ERP"
        detail="This record is not mirrored from a field application, so it has no external source."
      />
    );
  }

  return (
    <ul className="divide-y divide-line">
      {links.map((link) => (
        <li key={`${link.source_system}:${link.source_record_id}`} className="px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <span className="flex min-w-0 items-center gap-1.5">
              <RefreshCcw size={13} className="shrink-0 text-info" aria-hidden />
              <span className="code-text truncate">{link.source_system}</span>
            </span>
            {link.is_simulation ? <Status code="SIMULATION" /> : null}
          </div>

          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-small md:grid-cols-4">
            <div>
              <dt className="label-text">Source record</dt>
              <dd className="code-text truncate">{link.source_record_id}</dd>
            </div>
            <div>
              <dt className="label-text">Source path</dt>
              <dd className="code-text truncate">{link.source_entity}</dd>
            </div>
            <div>
              <dt className="label-text">Sync status</dt>
              <dd>
                {link.withdrawn_at ? (
                  <StatusBadge tone="neutral" icon={Ban}>
                    Withdrawn
                  </StatusBadge>
                ) : (
                  link.sync_status
                )}
              </dd>
            </div>
            <div>
              <dt className="label-text">Last sync</dt>
              <dd>{fmtDateTime(link.last_sync_at)}</dd>
            </div>
            <div>
              <dt className="label-text">Version</dt>
              <dd className="tabular">{link.sync_version}</dd>
            </div>
            <div>
              <dt className="label-text">Reconciliation</dt>
              <dd>{link.reconciliation_status}</dd>
            </div>
            <div className="col-span-2">
              <dt className="label-text">Contract</dt>
              <dd className="code-text truncate">{link.contract_version}</dd>
            </div>
          </dl>

          {link.correlation_id ? (
            <p className="mt-1.5 flex items-center gap-1 text-small text-ink-faint">
              <Link2 size={11} aria-hidden />
              <span className="code-text">{link.correlation_id}</span>
              <span>— everything that arrived in the same connector run</span>
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/* ── Exceptions ──────────────────────────────────────────────────────────────────────────────────── */

export function ExceptionsSection({ entityType, entityId }: { entityType: string; entityId: string }) {
  return (
    <div>
      <div className="border-b border-line px-4 py-2">
        <SectionLabel>Open disagreements between this record and its source</SectionLabel>
      </div>
      {/* Exceptions are timeline entries of a particular kind; reusing the timeline keeps one rendering
          of "what happened" rather than two that drift. */}
      <EntityTimeline
        entityType={entityType}
        entityId={entityId}
        kinds={['EXCEPTION']}
        emptyLabel="No exceptions"
      />
    </div>
  );
}
